import { useState } from 'react';
import { CalendarRange } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';

/**
 * Filters on `firstShare.sharedAt`. Native date inputs are used deliberately: they are
 * keyboard accessible, localised and screen-reader friendly out of the box, which a
 * hand-rolled calendar rarely is.
 */
export function DateRangeFilter({
  from,
  to,
  onChange,
  className,
}: {
  from: string | null;
  to: string | null;
  onChange: (range: { from: string | null; to: string | null }) => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const active = from !== null || to !== null;

  const summary = !active
    ? 'Any time'
    : from && to
      ? `${formatDate(from)} – ${formatDate(to)}`
      : from
        ? `From ${formatDate(from)}`
        : `Until ${formatDate(to)}`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {/* asChild targets the Button directly: an intervening span would receive the trigger's
          handlers and aria-expanded but is not focusable. */}
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn(
            'h-9 w-full justify-between gap-2 font-normal',
            active && 'border-primary',
            className,
          )}
          aria-label={`First shared: ${summary}`}
        >
          <span className="truncate text-left">
            <span className="text-muted-foreground">First shared: </span>
            <span className={cn(active && 'font-medium')}>{summary}</span>
          </span>
          <CalendarRange aria-hidden="true" className="h-4 w-4 shrink-0 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72">
        <fieldset className="flex flex-col gap-3">
          <legend className="mb-1 text-sm font-medium">First shared between</legend>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="filter-date-from">From</Label>
            <Input
              id="filter-date-from"
              type="date"
              value={from ?? ''}
              max={to ?? undefined}
              onChange={(event) => onChange({ from: event.target.value || null, to })}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="filter-date-to">To</Label>
            <Input
              id="filter-date-to"
              type="date"
              value={to ?? ''}
              min={from ?? undefined}
              onChange={(event) => onChange({ from, to: event.target.value || null })}
            />
          </div>

          {active ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                onChange({ from: null, to: null });
                setOpen(false);
              }}
            >
              Clear date range
            </Button>
          ) : null}
        </fieldset>
      </PopoverContent>
    </Popover>
  );
}
